import React, { useState, useMemo, useCallback } from 'react';
import {
    EmptyState, SectionTitle,
    getCategoryColor,
    formatPercent,
} from './analyticsHelpers';

// ─── Sort control ─────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
    { key: 'clicks',          label: 'Clicks' },
    { key: 'uniqueSessions',  label: 'Unique Sessions' },
    { key: 'sessionClickRate', label: 'Session Rate' },
    { key: 'name',            label: 'Name' },
];

const SortButton = ({ label, active, direction, onClick }) => (
    <button
        className={`sort-btn ${active ? 'active' : ''}`}
        onClick={onClick}
    >
        {label}
        {active && <span className="sort-arrow">{direction === 'asc' ? ' ↑' : ' ↓'}</span>}
    </button>
);

// ─── Full marker table row ────────────────────────────────────────────────────
const MarkerRow = ({ marker, rank, totalSessions, maxClicks }) => {
    const color = getCategoryColor(marker.category);
    const barWidth = maxClicks > 0 ? (marker.clicks / maxClicks) * 100 : 0;

    return (
        <div className="marker-table-row" style={{ '--rank-color': color }}>
            <span className="marker-rank">
                <span className="rank-num" style={{ color }}>{rank}</span>
                <span className="marker-name">{marker.name}</span>
            </span>
            <span>
                <span className="category-pill" style={{
                    background: color + '22',
                    color,
                }}>
                    {marker.category?.replace('_', ' ')}
                </span>
            </span>
            <span className="clicks-cell">
                <div className="clicks-bar-row">
                    <div
                        className="clicks-bar"
                        style={{ width: `${barWidth}%`, background: color }}
                    />
                    <strong className="clicks-count">{marker.clicks}</strong>
                </div>
            </span>
            <span className="unique-sessions-cell">{marker.uniqueSessions}</span>
            <span className="rate-cell">
                <div className="rate-bar-wrap">
                    <div
                        className="rate-bar"
                        style={{
                            width: `${Math.min(marker.sessionClickRate, 100)}%`,
                            background: color,
                        }}
                    />
                    <span className="rate-label">{marker.sessionClickRate}%</span>
                </div>
            </span>
        </div>
    );
};

// ─── Full category chart ──────────────────────────────────────────────────────
const FullCategoryChart = ({ data }) => {
    if (!data || Object.keys(data).length === 0) {
        return <EmptyState message="No click data yet" />;
    }
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const max = Math.max(...sorted.map(([, v]) => v), 1);
    const total = sorted.reduce((a, [, v]) => a + v, 0);

    return (
        <div className="category-chart full">
            {sorted.map(([cat, count]) => {
                const color = getCategoryColor(cat);
                return (
                    <div key={cat} className="cat-row full">
                        <span
                            className="cat-dot"
                            style={{ background: color }}
                        />
                        <span className="cat-name">{cat.replace('_', ' ')}</span>
                        <div className="cat-bar-wrap">
                            <div
                                className="cat-bar"
                                style={{
                                    width: `${(count / max) * 100}%`,
                                    background: color,
                                }}
                            />
                        </div>
                        <span className="cat-count">{count}</span>
                        <span className="cat-pct">{Math.round((count / total) * 100)}%</span>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Time-of-day heatmap grid ─────────────────────────────────────────────────
// Builds from raw clicks (passed as prop from shell's clicks fetch)
const TimeOfDayGrid = ({ clicks, markers }) => {
    const grid = useMemo(() => {
        if (!clicks || clicks.length === 0) return null;

        // Build markerName lookup
        const nameMap = {};
        (markers || []).forEach(m => { nameMap[m.markerId] = m.name; });

        // For each marker, count clicks per 4-hour block (0-4, 4-8, 8-12, 12-16, 16-20, 20-24)
        const blocks = ['0–4', '4–8', '8–12', '12–16', '16–20', '20–24'];
        const data = {}; // { markerId: [counts x 6] }

        clicks.forEach(c => {
            const id = c.markerId?._id || c.markerId;
            if (!id) return;
            const hour = new Date(c.timestamp).getHours();
            const block = Math.floor(hour / 4);
            if (!data[id]) data[id] = new Array(6).fill(0);
            data[id][block]++;
        });

        if (Object.keys(data).length === 0) return null;

        const rows = Object.entries(data)
            .map(([id, counts]) => ({
                id,
                name: nameMap[id] || 'Unknown',
                counts,
                total: counts.reduce((a, b) => a + b, 0),
            }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 10);

        const globalMax = Math.max(...rows.flatMap(r => r.counts), 1);

        return { rows, blocks, globalMax };
    }, [clicks, markers]);

    if (!grid) return <EmptyState message="No click timing data available" />;

    return (
        <div className="tod-grid-wrap">
            <div className="tod-grid">
                {/* Header */}
                <div className="tod-cell tod-header-blank" />
                {grid.blocks.map(b => (
                    <div key={b} className="tod-cell tod-header">{b}h</div>
                ))}
                {/* Rows */}
                {grid.rows.map(row => (
                    <React.Fragment key={row.id}>
                        <div className="tod-cell tod-row-label" title={row.name}>
                            {row.name.length > 14 ? row.name.slice(0, 13) + '…' : row.name}
                        </div>
                        {row.counts.map((count, i) => {
                            const intensity = count / grid.globalMax;
                            return (
                                <div
                                    key={i}
                                    className="tod-cell tod-data"
                                    style={{
                                        background: count === 0
                                            ? 'var(--bg)'
                                            : `rgba(43,142,232,${0.12 + intensity * 0.88})`,
                                        color: intensity > 0.5 ? 'white' : 'var(--text-2)',
                                    }}
                                    title={`${row.name} at ${grid.blocks[i]}: ${count} clicks`}
                                >
                                    {count > 0 ? count : ''}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

// ─── MarkersTab ───────────────────────────────────────────────────────────────
const MarkersTab = ({ analyticsData, clicks }) => {
    const { kpis, markerClickRanking, categoryClicks } = analyticsData;

    const [sortKey, setSortKey] = useState('clicks');
    const [sortDir, setSortDir] = useState('desc');
    const [filterCategory, setFilterCategory] = useState('all');

    const categories = useMemo(() => {
        const cats = new Set(markerClickRanking.map(m => m.category).filter(Boolean));
        return ['all', ...Array.from(cats).sort()];
    }, [markerClickRanking]);

    const handleSort = useCallback((key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    }, [sortKey]);

    const sortedMarkers = useMemo(() => {
        let list = markerClickRanking.slice();
        if (filterCategory !== 'all') {
            list = list.filter(m => m.category === filterCategory);
        }
        list.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (typeof av === 'string') {
                return sortDir === 'asc'
                    ? av.localeCompare(bv)
                    : bv.localeCompare(av);
            }
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return list;
    }, [markerClickRanking, sortKey, sortDir, filterCategory]);

    const maxClicks = useMemo(
        () => Math.max(...sortedMarkers.map(m => m.clicks), 1),
        [sortedMarkers]
    );

    return (
        <div className="ad-tab-content">

            {/* ── Full marker table ── */}
            <section className="ad-section">
                <div className="ad-card">
                    <div className="ad-card-header">
                        <SectionTitle>Marker Click Ranking</SectionTitle>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Category filter */}
                            <select
                                className="category-filter"
                                value={filterCategory}
                                onChange={e => setFilterCategory(e.target.value)}
                            >
                                {categories.map(c => (
                                    <option key={c} value={c}>
                                        {c === 'all' ? 'All categories' : c.replace('_', ' ')}
                                    </option>
                                ))}
                            </select>
                            {/* Sort buttons */}
                            {SORT_OPTIONS.map(opt => (
                                <SortButton
                                    key={opt.key}
                                    label={opt.label}
                                    active={sortKey === opt.key}
                                    direction={sortDir}
                                    onClick={() => handleSort(opt.key)}
                                />
                            ))}
                        </div>
                    </div>

                    {sortedMarkers.length === 0 ? (
                        <EmptyState message="No marker clicks recorded yet" />
                    ) : (
                        <>
                            <div className="marker-table-header">
                                <span>Marker</span>
                                <span>Category</span>
                                <span>Clicks</span>
                                <span>Unique Sessions</span>
                                <span>Session Rate</span>
                            </div>
                            {sortedMarkers.map((m, i) => (
                                <MarkerRow
                                    key={m.markerId}
                                    marker={m}
                                    rank={i + 1}
                                    totalSessions={kpis.totalSessions}
                                    maxClicks={maxClicks}
                                />
                            ))}
                        </>
                    )}
                </div>
            </section>

            {/* ── Category chart + Time-of-day grid ── */}
            <section className="ad-section ad-two-col">
                <div className="ad-card">
                    <SectionTitle sub="Total clicks grouped by marker type">Clicks by Category</SectionTitle>
                    <FullCategoryChart data={categoryClicks} />
                </div>
                <div className="ad-card">
                    <SectionTitle sub="When each marker was clicked (top 10 by volume)">Click Time of Day</SectionTitle>
                    <TimeOfDayGrid clicks={clicks} markers={markerClickRanking} />
                </div>
            </section>

        </div>
    );
};

export default MarkersTab;